import DatePicker, { DatePickerProps } from '@/shared/components/DatePicker'
import { SizeVariants } from '@/shared/forms/components/constants'
import { FieldHookConfig, useField, useFormikContext } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { formatDate, parseDate } from '@/lib/utils'
import { format, isValid } from 'date-fns'
import { CalendarDaysIcon } from '@heroicons/react/20/solid'
import DatePickerCalendar, {
  type DatePickerCalendarValue,
} from '@/shared/components/DatePickerCalendar'

type DatePickerRequiredFields = {
  label: string
  size: keyof typeof SizeVariants
  autocomplete: string
  disabled?: boolean
  placeholder?: string
  variant?: 'day-only' | 'month-and-year'
  fromDate?: Date
  toDate?: Date
} & FieldHookConfig<string> &
  Omit<DatePickerProps, 'onTouch'>

export default function FormikDatePicker({
  label,
  size,
  disabled = false,
  variant = 'day-only',
  appearance = 'default',
  fromDate,
  toDate,
  ...props
}: DatePickerRequiredFields) {
  const [field, meta, helpers] = useField(props)
  const { isSubmitting } = useFormikContext()
  const isDisabled = disabled || isSubmitting
  const inputId = props.id ?? field.name
  const [calendarOpen, setCalendarOpen] = useState(false)
  const calendarRootRef = useRef<HTMLDivElement>(null)
  const useBirthDateCalendar =
    appearance === 'portal-profile' && variant === 'month-and-year'
  const parsedDate = field.value ? parseDate(field.value) : null
  const calendarValue =
    parsedDate && isValid(parsedDate)
      ? {
          year: parsedDate.getFullYear(),
          month: parsedDate.getMonth() + 1,
          day: parsedDate.getDate(),
        }
      : undefined

  useEffect(() => {
    if (!calendarOpen) return

    function closeOnOutsideClick(event: PointerEvent) {
      if (
        event.target instanceof Node &&
        !calendarRootRef.current?.contains(event.target)
      ) {
        setCalendarOpen(false)
      }
    }

    document.addEventListener('pointerdown', closeOnOutsideClick)
    return () =>
      document.removeEventListener('pointerdown', closeOnOutsideClick)
  }, [calendarOpen])

  const onSelect = (_day: Date | undefined, selectedDay: Date) => {
    helpers
      .setValue(formatDate(selectedDay), false)
      .then(() => helpers.setTouched(true, true).then())
  }

  function selectBirthDate(value: DatePickerCalendarValue) {
    helpers
      .setValue(
        formatDate(new Date(value.year, value.month - 1, value.day)),
        false,
      )
      .then(() => helpers.setTouched(true, true).then())
    setCalendarOpen(false)
  }

  return (
    <div
      ref={calendarRootRef}
      className={SizeVariants[size as keyof typeof SizeVariants]}
    >
      <label
        htmlFor={inputId}
        className={clsx(
          'block text-sm font-medium leading-6',
          isDisabled ? 'text-gray-500' : 'text-gray-900',
        )}
      >
        {label}
      </label>
      {useBirthDateCalendar ? (
        <div className="relative mt-2">
          <button
            id={inputId}
            type="button"
            aria-expanded={calendarOpen}
            aria-haspopup="dialog"
            disabled={isDisabled}
            className={clsx(
              'flex h-[52px] w-full items-center rounded-[14px] border-[1.5px] border-transparent bg-[#F4F4F6] px-4 text-left text-[15px] leading-5 text-[#0B0B0F] outline-none transition-colors duration-300 focus:border-[#0B0B0F]/45 disabled:cursor-not-allowed disabled:opacity-60',
              !calendarValue && 'text-muted-foreground',
              meta.touched && meta.error && 'border-[#C6453C]',
            )}
            onClick={() => setCalendarOpen((current) => !current)}
          >
            {calendarValue && parsedDate
              ? format(parsedDate, 'd MMMM yyyy')
              : 'Pick a date'}
            <CalendarDaysIcon
              aria-hidden="true"
              className="ml-auto h-4 w-4 shrink-0 text-[#82828B]"
            />
          </button>
          {calendarOpen && (
            <div className="absolute left-0 top-[60px] z-50 sm:left-auto sm:right-0">
              <DatePickerCalendar
                value={calendarValue}
                defaultValue={calendarValue}
                startYear={fromDate?.getFullYear() ?? 1900}
                endYear={toDate?.getFullYear() ?? new Date().getFullYear()}
                initialView="year"
                onChange={selectBirthDate}
              />
            </div>
          )}
        </div>
      ) : (
        <DatePicker
          appearance={appearance}
          captionLayout={
            variant === 'day-only' ? 'buttons' : 'dropdown-buttons'
          }
          error={meta.touched ? meta.error : undefined}
          selected={!!field.value ? parseDate(field.value) : undefined}
          inputIsDisabled={isDisabled}
          onSelect={onSelect}
          defaultMonth={!!field.value ? parseDate(field.value) : undefined}
          onTouch={() => helpers.setTouched(true, false)}
          required
          id={inputId}
          variant={variant}
          fromDate={fromDate}
          toDate={toDate}
          {...props}
        />
      )}
      {meta.touched && meta.error && (
        <div className="mt-2 text-sm font-semibold text-red-600">
          {meta.error}
        </div>
      )}
    </div>
  )
}
